import { AppProctorGroups } from '../proctor/proctorGroups';
import React, { createContext, useContext, ReactNode } from 'react';

/**
 * Datadog RUM configuration passed from server to client
 */
export interface RumConfig {
    /** Datadog RUM Application ID */
    applicationId: string;
    /** Datadog client token (public, safe for browser) */
    clientToken: string;
    /** Datadog site (e.g., 'datadoghq.com') */
    site: string;
    /** Service name (must match APM service name for correlation) */
    service: string;
    /** Environment name (e.g., 'prod', 'qa', 'local') */
    env: string;
    /** Application version */
    version: string;
}

/**
 * Initial state values from server-side rendering
 */
export interface InitialState {
    /** Current route path */
    route: string;
    /** Site country code (geoCountry) from server */
    countryCode: string;
    /** IP-based country code (ipCountry) from server */
    ipCountryCode: string;
    /** Locale string (e.g., 'en-US', 'ja-JP') from server */
    locale: string;
    /** Indeed Profile base URL for building profile-related URLs (environment-dependent) */
    indeedProfileBaseUrl: string;
    /** GraphQL endpoint URL from config files */
    graphqlEndpoint?: string;
    /** OneGraph API key from config files (required for client-side GraphQL requests) */
    oneGraphApiKey?: string;
    /** Homepage URL for return navigation (environment-dependent) */
    homepageUrl: string;
    /** My Jobs base URL, no trailing slash (environment-dependent); pages append the tab path */
    myJobsUrl: string;
    /** Current origin (protocol + hostname, e.g., 'https://example.com') for building absolute URLs in SSR-safe way */
    origin: string;
    /** Whether the flow is loaded in the Job Search mobile app */
    isJobSearchApp: boolean;
    /**
     * Where the user arrived from (e.g. 'HOMEPAGE', 'MYJOBS', or a verbatim
     * `?from=` value), resolved server-side by getEntryPoint. Optional for
     * back-compat with test fixtures; the server always populates it.
     */
    entryPoint?: string;
    /** Validated return URL for navigation (server-validated to prevent open redirect) */
    returnUrl: string;
    /** Indeed base URL for legal and other static pages (environment-dependent) */
    indeedBaseUrl: string;
    /** Smart Apply webapp base URL for edit redirect (environment-dependent) */
    smartApplyBaseUrl: string;
    /** Preview webapp base URL for downloading cached preview HTML (environment-dependent) */
    previewWebappBaseUrl: string;
    /** Setup Log UID for logging */
    setupLogUid?: string;
    /** Review Log UID for auto-apply review flow logging */
    reviewLogUid?: string;

    proctorGroups: AppProctorGroups;
    /** Application name from config files - Used for OneGraph Router migration (IAFE-430) */
    appName: string;
    /** Datadog RUM configuration (optional - only present when RUM is enabled) */
    rumConfig?: RumConfig;

    ctk?: string;
}

/**
 * Context for initial state
 * Default value is null - useInitialState will throw if used outside provider
 */
const InitialStateContext = createContext<InitialState | null>(null);

/**
 * Props for InitialStateProvider
 */
interface InitialStateProviderProps {
    children: ReactNode;
    /** Initial state from server or client entry point - Required */
    initialState: InitialState;
}

/**
 * Provider component for InitialStateContext
 *
 * Receives initial state from props (passed from server or client entry point).
 * Does not read from window object directly - ensures single source of truth.
 *
 * @param initialState - Required initial state from server-side rendering or client hydration
 */
export const InitialStateProvider: React.FC<InitialStateProviderProps> = ({
    children,
    initialState,
}) => {
    return (
        <InitialStateContext.Provider value={initialState}>{children}</InitialStateContext.Provider>
    );
};

/**
 * Hook to access initial state from context
 *
 * @throws Error if used outside of InitialStateProvider
 * @example
 * ```tsx
 * const { countryCode, ipCountryCode, locale, indeedProfileBaseUrl, origin } = useInitialState();
 * ```
 */
export const useInitialState = (): InitialState => {
    const context = useContext(InitialStateContext);
    if (context === null) {
        throw new Error('useInitialState must be used within InitialStateProvider');
    }
    return context;
};
